import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Terminal, ExternalLink, Info, Loader2 } from "lucide-react";

export interface LaunchCodespaceButtonProps {
  /**
   * Full repository identifier (e.g. "Codehub-Technologies/python-codehub-assignments-bundle-username")
   * or short repository name (resolved with defaultOrg or repoUrl).
   */
  repoFullName?: string | null;
  /**
   * Direct GitHub Codespaces creation URL returned by the backend (`codespace_url`).
   */
  codespaceUrl?: string | null;
  /**
   * Optional GitHub repository HTML URL fallback (e.g. "https://github.com/Org/repo-name").
   */
  repoUrl?: string | null;
  /**
   * Fallback GitHub organization if repoFullName does not include an owner prefix.
   */
  defaultOrg?: string;
  /**
   * Target git branch to launch in Codespaces (defaults to "main").
   */
  refBranch?: string;
  /**
   * Explicitly disable the button (e.g. while repository generation is in progress).
   */
  disabled?: boolean;
  /**
   * Show loading indicator while repository provisioning is running.
   */
  isLoading?: boolean;
  /**
   * Render the visible helper caption underneath the button in addition to the hover tooltip.
   */
  showHelperNote?: boolean;
  /**
   * Visual theme matching GitHub Codespaces ("green": #238636, "dark": #21262d).
   */
  variant?: "green" | "dark";
  /**
   * Button sizing preset.
   */
  size?: "sm" | "md";
  /**
   * Custom button label (defaults to "Open in Codespaces").
   */
  label?: string;
  /**
   * Optional container className.
   */
  className?: string;
}

const HELPER_TEXT =
  "Launches a pre-configured VS Code environment in your browser. No local installation required.";

/**
 * Ensures a GitHub owner or repository segment conforms strictly to GitHub URL-safe characters.
 */
export function sanitizeGitHubSegment(segment?: string | null): string {
  if (!segment) return "";
  const trimmed = String(segment)
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .replace(/\.git$/i, "");
  return trimmed.replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
}

/**
 * Resolves a validated, URL-safe GitHub Codespaces launch URL from either a backend
 * `codespace_url`, a `repoFullName` ("Org/repo"), or a `repoUrl`.
 */
export function resolveCodespaceLaunchUrl({
  codespaceUrl,
  repoFullName,
  repoUrl,
  defaultOrg = "Codehub-Technologies",
  refBranch = "main",
}: {
  codespaceUrl?: string | null;
  repoFullName?: string | null;
  repoUrl?: string | null;
  defaultOrg?: string;
  refBranch?: string;
}): string | null {
  // 1. Validate direct backend codespace_url if provided
  if (codespaceUrl && typeof codespaceUrl === "string") {
    const trimmedUrl = codespaceUrl.trim();
    if (trimmedUrl.startsWith("https://github.com/codespaces/")) {
      return trimmedUrl;
    }
  }

  // 2. Resolve owner and repo name from repoFullName or repoUrl
  let rawOwner = "";
  let rawRepo = "";

  if (repoFullName && typeof repoFullName === "string") {
    const cleanFull = repoFullName.trim().replace(/^\/+|\/+$/g, "");
    if (cleanFull.includes("/")) {
      const parts = cleanFull.split("/").filter(Boolean);
      if (parts.length >= 2) {
        rawOwner = parts[0];
        rawRepo = parts[parts.length - 1];
      }
    } else if (cleanFull) {
      rawRepo = cleanFull;
    }
  }

  if ((!rawOwner || !rawRepo) && repoUrl && typeof repoUrl === "string") {
    const match = repoUrl
      .trim()
      .match(/github\.com\/([^/?#]+)\/([^/?#]+)/i);
    if (match) {
      if (!rawOwner) rawOwner = match[1];
      if (!rawRepo) rawRepo = match[2];
    }
  }

  if (!rawOwner && rawRepo) {
    rawOwner = defaultOrg;
  }

  const safeOwner = sanitizeGitHubSegment(rawOwner);
  const safeRepo = sanitizeGitHubSegment(rawRepo);
  const safeRef = sanitizeGitHubSegment(refBranch) || "main";

  if (!safeOwner || !safeRepo) {
    return null;
  }

  const encodedRepoParam = `${encodeURIComponent(safeOwner)}/${encodeURIComponent(safeRepo)}`;
  return `https://github.com/codespaces/new?hide_repo_select=true&ref=${encodeURIComponent(
    safeRef
  )}&repo=${encodedRepoParam}`;
}

export const LaunchCodespaceButton: React.FC<LaunchCodespaceButtonProps> = ({
  repoFullName,
  codespaceUrl,
  repoUrl,
  defaultOrg = "Codehub-Technologies",
  refBranch = "main",
  disabled = false,
  isLoading = false,
  showHelperNote = true,
  variant = "green",
  size = "md",
  label = "Open in Codespaces",
  className = "",
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const resolvedUrl = resolveCodespaceLaunchUrl({
    codespaceUrl,
    repoFullName,
    repoUrl,
    defaultOrg,
    refBranch,
  });

  const isUnavailable = disabled || isLoading || !resolvedUrl;

  const sizeClasses =
    size === "sm"
      ? "cht-px-3.5 cht-py-2 cht-text-xs cht-rounded-xl cht-gap-1.5"
      : "cht-px-4 cht-py-2.5 cht-text-xs sm:cht-text-sm cht-rounded-[12px] cht-gap-2";

  const themeClasses =
    variant === "dark"
      ? "cht-bg-[#21262d] hover:cht-bg-[#30363d] cht-text-white cht-border cht-border-[#30363d]"
      : "cht-bg-[#238636] hover:cht-bg-[#2ea043] cht-text-white cht-border cht-border-[#2ea043]/60";

  const buttonContent = (
    <>
      {isLoading ? (
        <Loader2 size={size === "sm" ? 13 : 15} className="cht-animate-spin cht-shrink-0" />
      ) : (
        <Terminal size={size === "sm" ? 13 : 15} className="cht-shrink-0" />
      )}
      <span className="cht-font-bold cht-tracking-tight cht-whitespace-nowrap">
        {isLoading
          ? "Preparing Codespace..."
          : !resolvedUrl
            ? "Codespace Unavailable"
            : label}
      </span>
      {!isLoading && resolvedUrl && (
        <ExternalLink
          size={size === "sm" ? 12 : 13}
          className="cht-opacity-85 cht-shrink-0"
        />
      )}
    </>
  );

  return (
    <div className={`cht-inline-flex cht-flex-col ${className}`}>
      <div
        className="cht-relative cht-inline-flex cht-items-center"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
      >
        {isUnavailable ? (
          <button
            type="button"
            disabled
            aria-disabled="true"
            title={HELPER_TEXT}
            className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} cht-bg-[#21262d]/60 cht-text-slate-400 cht-border cht-border-slate-700/60 cht-cursor-not-allowed cht-opacity-65 cht-select-none cht-transition-all`}
          >
            {buttonContent}
          </button>
        ) : (
          <motion.a
            href={resolvedUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={HELPER_TEXT}
            aria-label={`${label} — ${HELPER_TEXT}`}
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.985 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} ${themeClasses} cht-shadow-[0_2px_10px_rgba(35,134,54,0.22)] cht-transition-colors cht-no-underline focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#2ea043]/60`}
          >
            {buttonContent}
          </motion.a>
        )}

        {/* Accessible Hover / Focus Tooltip */}
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              role="tooltip"
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="cht-absolute cht-bottom-full cht-left-1/2 -cht-translate-x-1/2 cht-mb-2 cht-w-64 cht-p-2.5 cht-rounded-xl cht-bg-[#161b22] cht-text-slate-100 cht-border cht-border-[#30363d] cht-shadow-xl cht-z-30 cht-pointer-events-none"
            >
              <div className="cht-flex cht-items-start cht-gap-2">
                <Info size={13} className="cht-text-emerald-400 cht-shrink-0 cht-mt-0.5" />
                <p className="cht-text-[11px] cht-leading-snug cht-font-medium cht-text-slate-200">
                  {HELPER_TEXT}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showHelperNote && (
        <p className="cht-mt-1.5 cht-text-[11px] cht-text-slate-400 cht-flex cht-items-center cht-gap-1.5 cht-leading-snug">
          <Info size={11} className="cht-text-emerald-400 cht-shrink-0" />
          <span>{HELPER_TEXT}</span>
        </p>
      )}
    </div>
  );
};

export default LaunchCodespaceButton;
